import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import getFetchShipOfferingsOptions from '@nike/shop-apis/buy/fulfillment_offerings/v1/fetch-ship-offerings/get-options';
import getFetchPickupOfferingOptions from '@nike/shop-apis/buy/fulfillment_offerings/v1/fetch-pickup-offering/get-options';
import getFetchStoreLocationsOptions from '@nike/shop-apis/store/store_locations/v1/get-options';
import getFetchIdentityOptions from '@nike/shop-apis/identity/web-shell-client/get-options';
import getFetchIdentityUserAddressOptions from '@nike/shop-apis/identity/user/v1/address/get-options';
import { selectPreferredAddress } from '@nike/shop-apis/identity/user/v1/address/select';
import { selectFirstStoreLocationWithBOPISEnabled } from '@nike/shop-apis/store/store_locations/v1/select';

/**
 * @memberof NikeShop
 * @name NikeShop.getBOPIS_EDD_Info
 * @description Fetches shipping and pickup fulfillment information for a product on PDP
 * Returns data needed to display shipping estimates and pickup availability
 * Automatically determines all parameters (postal code, coordinates, store location) from:
 * - Retail MFE store (localStorage from store locator interactions)
 * - User's identity address (if logged in)
 * @param {object} params - named parameters
 * @param {string} params.skuId - SKU identifier, unique UUID of the SKU
 * @param {string} params.productId - Product identifier
 * @returns {Promise<Object>} Object containing ship and pickup fulfillment data
 * @returns {Object} return.ship - Shipping fulfillment data
 * @returns {string} return.ship.maxDate - Maximum ship date (ISO 8601)
 * @returns {string} return.ship.minDate - Minimum ship date (ISO 8601)
 * @returns {string} return.ship.timeZone - Timezone for ship dates
 * @returns {number} return.ship.shipPrice - Shipping price
 * @returns {Object} return.pickup - Pickup fulfillment data
 * @returns {string} return.pickup.maxPickupDate - Maximum pickup date (ISO 8601)
 * @returns {string} return.pickup.minPickupDate - Minimum pickup date (ISO 8601)
 * @returns {string} return.pickup.pickupLocation - Store name for pickup
 * @returns {string} return.pickup.locationId - Store location ID
 * @returns {string} return.pickup.timeZone - Timezone for pickup dates
 *
 * @example
 * // Get fulfillment info - all parameters automatically determined
 * const fulfillmentInfo = await NikeShop.getBOPIS_EDD_Info({
 *   skuId: '38e0f7bd-fca8-5ad5-93f2-7ba884b7283d',
 *   productId: 'product-uuid'
 * });
 *
 * @example
 * // Returns shipping and pickup data
 * // {
 * //   ship: {
 * //     maxDate: "2025-11-20T12:00:00Z",
 * //     minDate: "2025-11-18T12:00:00Z",
 * //     timeZone: "America/Los_Angeles",
 * //     shipPrice: 7.00
 * //   },
 * //   pickup: {
 * //     maxPickupDate: "2025-11-15T12:00:00Z",
 * //     minPickupDate: "2025-11-14T12:00:00Z",
 * //     pickupLocation: "Nike Portland",
 * //     locationId: "store-uuid",
 * //     timeZone: "America/Los_Angeles"
 * //   }
 * // }
 */
var getBOPIS_EDD_Info = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
    var _stores$retailMFE, _retailMFEState$locat, _retailMFEState$locat2;
    var skuId, productId, _ref$offeringTypes, offeringTypes, _window$NikeShop$__IN, queryClient, stores, includesShip, includesPickup, retailMFEState, selectedStore, selectedStoreLocationId, mfeStoreName, _ref3, longitude, latitude, retailMFEPostalCode, postalCode, identity, addresses, preferredAddress, storeLocationsPromise, shouldFetchStoreLocations, shipOfferingsPromise, _yield$Promise$all, _yield$Promise$all2, shipOfferings, storeLocations, firstStoreName, pickupOfferings, resolvedLocationId, firstStoreFromLocations, shipResponse, pickupResponse, _shipOfferings$items, _shipData$getBy, _shipData$getBy2, _shipData$getBy3, _shipData$price, shipData, _pickupOfferings, _pickupData$getBy, _pickupData$getBy2, _pickupData$location, _pickupData$getBy3, pickupData, _t;
    return _regeneratorRuntime.wrap(function (_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          skuId = _ref.skuId, productId = _ref.productId, _ref$offeringTypes = _ref.offeringTypes, offeringTypes = _ref$offeringTypes === void 0 ? ['SHIP', 'PICKUP'] : _ref$offeringTypes;
          _window$NikeShop$__IN = window.NikeShop.__INTERNAL_USE__, queryClient = _window$NikeShop$__IN.queryClient, stores = _window$NikeShop$__IN.stores;
          includesShip = offeringTypes.includes('SHIP');
          includesPickup = offeringTypes.includes('PICKUP'); // Get selected store and coordinates from retail MFE store (localStorage)
          retailMFEState = (stores === null || stores === void 0 || (_stores$retailMFE = stores.retailMFE) === null || _stores$retailMFE === void 0 || (_stores$retailMFE = _stores$retailMFE.store) === null || _stores$retailMFE === void 0 ? void 0 : _stores$retailMFE.getState()) || {};
          selectedStore = retailMFEState.selectedStore;
          selectedStoreLocationId = selectedStore === null || selectedStore === void 0 ? void 0 : selectedStore.id;
          mfeStoreName = selectedStore === null || selectedStore === void 0 ? void 0 : selectedStore.name;
          _ref3 = ((_retailMFEState$locat = retailMFEState.locationSearch) === null || _retailMFEState$locat === void 0 ? void 0 : _retailMFEState$locat.coordinates) || {}, longitude = _ref3.longitude, latitude = _ref3.latitude;
          retailMFEPostalCode = (_retailMFEState$locat2 = retailMFEState.locationSearch) === null || _retailMFEState$locat2 === void 0 ? void 0 : _retailMFEState$locat2.postCode; // Determine postal code from Retail MFE store or user identity address
          postalCode = retailMFEPostalCode; // Only fetch identity address if
          // 1. includes pickup and we don't have a selected store location id or postal code or coordinates
          // 2. includes ship and we don't have a postal code
          if (!(includesPickup && (!selectedStoreLocationId || !postalCode || !(longitude && latitude)) || includesShip && !postalCode)) {
            _context.next = 3;
            break;
          }
          _context.next = 1;
          return queryClient.fetchQuery(getFetchIdentityOptions());
        case 1:
          identity = _context.sent;
          if (!(identity !== null && identity !== void 0 && identity.isLoggedIn)) {
            _context.next = 3;
            break;
          }
          _context.next = 2;
          return queryClient.fetchQuery(getFetchIdentityUserAddressOptions());
        case 2:
          addresses = _context.sent;
          preferredAddress = selectPreferredAddress(addresses); // set postal code from user's preferred address
          postalCode = preferredAddress === null || preferredAddress === void 0 ? void 0 : preferredAddress.code;
        case 3:
          // Fetch store locations only if includes pickup and don't have a locationId or selected store and have coordinates or postal code
          storeLocationsPromise = Promise.resolve(null);
          shouldFetchStoreLocations = includesPickup && !selectedStoreLocationId && (longitude && latitude || postalCode);
          if (shouldFetchStoreLocations) {
            storeLocationsPromise = queryClient.fetchQuery(getFetchStoreLocationsOptions({
              anchor: 0,
              count: 25,
              longitude: longitude,
              latitude: latitude,
              postalCode: postalCode,
              queryClient: queryClient
            }));
          }

          // Fetch ship offerings
          shipOfferingsPromise = Promise.resolve(null);
          if (includesShip) {
            shipOfferingsPromise = queryClient.fetchQuery(getFetchShipOfferingsOptions({
              skuId: skuId,
              productId: productId,
              postalCode: postalCode,
              queryClient: queryClient
            }));
          }

          // Wait for store locations and ship offerings
          _context.next = 4;
          return Promise.all([shipOfferingsPromise, storeLocationsPromise]);
        case 4:
          _yield$Promise$all = _context.sent;
          _yield$Promise$all2 = _slicedToArray(_yield$Promise$all, 2);
          shipOfferings = _yield$Promise$all2[0];
          storeLocations = _yield$Promise$all2[1];
          firstStoreName = null, pickupOfferings = null, resolvedLocationId = null;
          if (!includesPickup) {
            _context.next = 8;
            break;
          }
          // Determine which location ID to use for pickup
          firstStoreFromLocations = selectFirstStoreLocationWithBOPISEnabled(storeLocations);
          resolvedLocationId = selectedStoreLocationId || (firstStoreFromLocations === null || firstStoreFromLocations === void 0 ? void 0 : firstStoreFromLocations.id);
          firstStoreName = firstStoreFromLocations === null || firstStoreFromLocations === void 0 ? void 0 : firstStoreFromLocations.name;

          // Fetch pickup offerings if offeringTypes includes PICKUP and we have a location ID
          if (!(includesPickup && resolvedLocationId)) {
            _context.next = 8;
            break;
          }
          _context.prev = 5;
          _context.next = 6;
          return queryClient.fetchQuery(getFetchPickupOfferingOptions({
            skuId: skuId,
            productId: productId,
            locationId: resolvedLocationId,
            locationType: 'store/store_views',
            queryClient: queryClient
          }));
        case 6:
          pickupOfferings = _context.sent;
          _context.next = 8;
          break;
        case 7:
          _context.prev = 7;
          _t = _context["catch"](5);
        case 8:
          if (includesShip) {
            shipData = shipOfferings === null || shipOfferings === void 0 || (_shipOfferings$items = shipOfferings.items) === null || _shipOfferings$items === void 0 || (_shipOfferings$items = _shipOfferings$items[0]) === null || _shipOfferings$items === void 0 || (_shipOfferings$items = _shipOfferings$items.fulfillmentOfferings) === null || _shipOfferings$items === void 0 ? void 0 : _shipOfferings$items[0];
            shipResponse = {
              maxDate: shipData === null || shipData === void 0 || (_shipData$getBy = shipData.getBy) === null || _shipData$getBy === void 0 || (_shipData$getBy = _shipData$getBy.maxDate) === null || _shipData$getBy === void 0 ? void 0 : _shipData$getBy.dateTime,
              minDate: shipData === null || shipData === void 0 || (_shipData$getBy2 = shipData.getBy) === null || _shipData$getBy2 === void 0 || (_shipData$getBy2 = _shipData$getBy2.minDate) === null || _shipData$getBy2 === void 0 ? void 0 : _shipData$getBy2.dateTime,
              timeZone: shipData === null || shipData === void 0 || (_shipData$getBy3 = shipData.getBy) === null || _shipData$getBy3 === void 0 || (_shipData$getBy3 = _shipData$getBy3.maxDate) === null || _shipData$getBy3 === void 0 ? void 0 : _shipData$getBy3.timezone,
              shipPrice: shipData === null || shipData === void 0 || (_shipData$price = shipData.price) === null || _shipData$price === void 0 ? void 0 : _shipData$price.total
            };
          }

          // Extract pickup data
          if (includesPickup) {
            pickupData = (_pickupOfferings = pickupOfferings) === null || _pickupOfferings === void 0 || (_pickupOfferings = _pickupOfferings.items) === null || _pickupOfferings === void 0 || (_pickupOfferings = _pickupOfferings[0]) === null || _pickupOfferings === void 0 || (_pickupOfferings = _pickupOfferings.fulfillmentOfferings) === null || _pickupOfferings === void 0 ? void 0 : _pickupOfferings[0];
            pickupResponse = {
              maxPickupDate: pickupData === null || pickupData === void 0 || (_pickupData$getBy = pickupData.getBy) === null || _pickupData$getBy === void 0 || (_pickupData$getBy = _pickupData$getBy.maxDate) === null || _pickupData$getBy === void 0 ? void 0 : _pickupData$getBy.dateTime,
              minPickupDate: pickupData === null || pickupData === void 0 || (_pickupData$getBy2 = pickupData.getBy) === null || _pickupData$getBy2 === void 0 || (_pickupData$getBy2 = _pickupData$getBy2.minDate) === null || _pickupData$getBy2 === void 0 ? void 0 : _pickupData$getBy2.dateTime,
              pickupLocation: (pickupData === null || pickupData === void 0 || (_pickupData$location = pickupData.location) === null || _pickupData$location === void 0 ? void 0 : _pickupData$location.name) || mfeStoreName || firstStoreName,
              locationId: resolvedLocationId,
              timeZone: pickupData === null || pickupData === void 0 || (_pickupData$getBy3 = pickupData.getBy) === null || _pickupData$getBy3 === void 0 || (_pickupData$getBy3 = _pickupData$getBy3.maxDate) === null || _pickupData$getBy3 === void 0 ? void 0 : _pickupData$getBy3.timezone
            };
          }
          return _context.abrupt("return", {
            ship: shipResponse,
            pickup: pickupResponse
          });
        case 9:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[5, 7]]);
  }));
  return function getBOPIS_EDD_Info(_x) {
    return _ref2.apply(this, arguments);
  };
}();
export default getBOPIS_EDD_Info;